"use client";

import { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useGitHubContributions } from "./use-github-contributions";

// Auto-refresh interval (6 hours)
const AUTO_REFRESH_INTERVAL = 6 * 60 * 60 * 1000;

// Helper function to get the appropriate CSS class based on contribution count
const getContributionColorClass = (count: number): string => {
  if (count === 0) return "bg-neutral-100 dark:bg-neutral-800";
  if (count <= 3) return "bg-green-100";
  if (count <= 8) return "bg-green-200";
  if (count <= 12) return "bg-green-300";
  return "bg-green-400";
};

export function ContributionGraph() {
  const { data, loading, error, refreshing, refreshData, isDataStale } =
    useGitHubContributions();

  const [selectedDataSource, setSelectedDataSource] = useState<
    "personal" | "enterprise" | "merged"
  >("merged");
  const [lastRefreshAttempt, setLastRefreshAttempt] = useState<number>(0);

  // Auto-refresh mechanism that will run on component mount
  useEffect(() => {
    if (!data) return;

    const checkAndAutoRefresh = async () => {
      // Only attempt to refresh if we haven't tried in the last hour
      // This prevents multiple refresh attempts in a short period
      if (Date.now() - lastRefreshAttempt > 1 * 60 * 60 * 1000) {
        // Check if data is stale
        if (isDataStale()) {
          console.log("Auto-refreshing GitHub contribution data...");
          await refreshData();
          setLastRefreshAttempt(Date.now());
        }
      }
    };

    // Initial check
    if (!refreshing) {
      checkAndAutoRefresh();
    }

    // Set up periodic refresh
    const autoRefreshTimer = setInterval(() => {
      checkAndAutoRefresh();
    }, AUTO_REFRESH_INTERVAL);

    // Clear interval on component unmount
    return () => {
      clearInterval(autoRefreshTimer);
    };
  }, [data, refreshing, lastRefreshAttempt, isDataStale, refreshData]);

  // Set the default data source when data changes
  useEffect(() => {
    if (data) {
      if (data.merged) {
        setSelectedDataSource("merged");
      } else if (data.personal) {
        setSelectedDataSource("personal");
      } else if (data.enterprise) {
        setSelectedDataSource("enterprise");
      }
    }
  }, [data]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">GitHub Contributions</h2>
          <div className="h-2 w-16 animate-pulse rounded bg-neutral-100 dark:bg-neutral-800"></div>
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-6 rounded-full" />
        </div>
        <Skeleton className="h-32 w-full" />
        <p className="text-foreground text-xs">Loading contributions...</p>
      </div>
    );
  }

  if (error) {
    return;
  }

  const getActiveCalendar = () => {
    if (!data) return null;
    return data[selectedDataSource];
  };

  const calendar = getActiveCalendar();

  if (!calendar) {
    return (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-950">
        <p className="text-sm text-yellow-600 dark:text-yellow-400">
          No contribution data available for the selected source
        </p>
      </div>
    );
  }

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Get month labels with positions
  const monthLabels = () => {
    const labels: { month: string; position: number }[] = [];
    let currentMonth = -1;
    let weekIndex = 0;

    calendar.weeks.forEach((week, index) => {
      if (week.contributionDays.length > 0) {
        const date = new Date(week.contributionDays[0].date);
        const month = date.getMonth();

        if (month !== currentMonth) {
          currentMonth = month;
          labels.push({
            month: months[month],
            position: weekIndex,
          });
        }

        weekIndex++;
      }
    });

    return labels;
  };

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="font-bold md:text-lg">GitHub Contributions</h2>
        {isDataStale() && (
          <div className="flex items-center">
            <span className="text-foreground mr-2 text-xs">Data is stale</span>
            <button
              onClick={() => {
                refreshData();
                setLastRefreshAttempt(Date.now());
              }}
              disabled={refreshing}
              className="dark:hover:bg-foreground-800 rounded-md border px-2 py-1 text-xs text-neutral-600 transition-colors hover:bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-400"
            >
              {refreshing ? "Updating..." : "Update"}
            </button>
          </div>
        )}
      </div>

      <div className="w-full">
        <div className="relative flex min-w-full pt-4 pb-2">
          {/* Month labels */}
          <div className="absolute top-0 right-0 left-0 hidden md:right-8 md:block">
            {monthLabels().map((label, index) => (
              <span
                key={index}
                className="text-foreground absolute text-xs"
                style={{
                  left: `${(label.position / calendar.weeks.length) * 100}%`,
                }}
              >
                {label.month}
              </span>
            ))}
          </div>

          {/* Contribution grid */}
          <div className="grid w-full auto-cols-fr grid-flow-col gap-1">
            {calendar.weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="flex flex-col gap-1">
                {Array.from({ length: 7 }).map((_, dayIndex) => {
                  const day = week.contributionDays.find(
                    (day) => new Date(day.date).getDay() === dayIndex,
                  );
                  const contributionCount = day?.contributionCount || 0;

                  return (
                    <div
                      key={dayIndex}
                      className={cn(
                        "size-1 rounded-sm md:size-2",
                        getContributionColorClass(contributionCount),
                      )}
                      title={
                        day
                          ? `${day.contributionCount} contributions on ${new Date(
                              day.date,
                            ).toLocaleDateString()}`
                          : "No contributions"
                      }
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="text-foreground flex items-center justify-end gap-2 text-xs">
        <span>Less</span>
        <div className="flex gap-1">
          <div className="h-3 w-3 rounded-sm bg-neutral-100 dark:bg-neutral-800" />
          <div className="h-3 w-3 rounded-sm bg-green-100" />
          <div className="h-3 w-3 rounded-sm bg-green-200" />
          <div className="h-3 w-3 rounded-sm bg-green-300" />
          <div className="h-3 w-3 rounded-sm bg-green-400" />
        </div>
        <span>More</span>
      </div>
    </div>
  );
}
